import type {
  LoadTestConfig,
  LoadTestStatus,
  TestListItem,
  GetClientsResponse,
  ClientWithAudio,
  PercentileStats,
  LoadTestTargetPreview,
} from "../../services/api.service";
import { LOAD_TEST_DEFAULTS } from "../../config/api.config";

// Re-export types from api.service for convenience
export type {
  LoadTestConfig,
  LoadTestStatus,
  TestListItem,
  GetClientsResponse,
  ClientWithAudio,
  PercentileStats,
  LoadTestTargetPreview,
};

// Tab types
export type ActiveTab = "config" | "running" | "results" | "latency";

// Audio player state
export interface AudioPlayerState {
  playingClientId: number | null;
  audioUrl: string | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
}

// Utility functions
export const getDefaultConfig = (): LoadTestConfig => {
  return {
    num_clients: 5,
    turns: 5,
    classroom_id: LOAD_TEST_DEFAULTS.classroom_id,
    password: LOAD_TEST_DEFAULTS.password,
    // Default to the original behavior: all clients start at once, fixed turns.
    traffic_pattern: "all_at_once",
    pattern_window_seconds: 60,
    batch_size: 5,
    stop_mode: "turns",
    duration_seconds: 300,
    // ~90s lead time so Fargate tasks boot before their arrival slots.
    boot_buffer_seconds: 90,
  };
};

export const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms.toFixed(0)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const formatOptionalDuration = (
  ms: number | null | undefined,
): string => {
  return isFiniteNumber(ms) ? formatDuration(ms) : "N/A";
};

export const formatTime = (seconds: number): string => {
  if (isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Format an ISO timestamp as a short PST/PDT date+time for the history list.
// Returns "—" when the timestamp is missing.
export const formatPstDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
};

// Flatten a (possibly nested) array of per-turn latency samples into a flat
// list of finite numbers. Mirrors the backend's _coerce_samples so per-client
// numbers line up with the pooled summary percentiles.
export const flattenSamples = (
  values: (number | number[])[] | undefined,
): number[] => {
  if (!values) return [];
  const out: number[] = [];
  for (const value of values) {
    if (Array.isArray(value)) {
      out.push(...flattenSamples(value));
    } else if (typeof value === "number" && Number.isFinite(value)) {
      out.push(value);
    }
  }
  return out;
};

// Median of the samples, or null when there are none. Used for the per-client
// latency tiles, where one representative number is clearer than p50/p90/p95.
export const median = (
  values: (number | number[])[] | undefined,
): number | null => {
  const samples = flattenSamples(values).sort((a, b) => a - b);
  if (samples.length === 0) return null;
  const mid = Math.floor(samples.length / 2);
  if (samples.length % 2 === 0) {
    return ((samples[mid - 1] as number) + (samples[mid] as number)) / 2;
  }
  return samples[mid] as number;
};
